import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { Nullable } from 'apps/core/types/global.types';
import { WebStorageKeys, type PagePreferences } from 'apps/core/types/web-storage.types';
import { removeUserWebStorage, setUserWebStorage } from 'utils/user-web-storage';

interface PageState {
  preferenceId: Nullable<keyof PagePreferences>;
  showLegacy: boolean;
  isFilterVisible: boolean;
  filterOptions: Nullable<PageFilterOptions>;
  entriesRanges: number[];
  entriesRange: number;
  entriesLimit: Nullable<number>;
  savedFilters: SavedFilter[];
}

export enum InputTypes {
  KEYWORD = 'KEYWORD',
  LIST = 'LIST',
  TOGGLE = 'TOGGLE',
  SELECT = 'SELECT',
  DATE = 'DATE',
}

type PageFilterOptionItem =
  | string
  | {
      text: string;
      value: string;
    };

export type PageFilterOptionList = PageFilterOptionItem[];

export type PageFilterOption = {
  id: string;
  type: InputTypes;
  label: string;
  defaultOptions?: PageFilterOptionList;
  defaultValue?: string | boolean | number | null;
  value?: string | boolean | number | null;
};

export type SavedFilter = {
  label: string;
  filterOptions: PageFilterOptions;
};

export type PageFilterOptions = PageFilterOption[];

const initialState: PageState = {
  preferenceId: null,
  showLegacy: false,
  isFilterVisible: false,
  filterOptions: [],
  entriesRanges: [10, 20, 50, 100, 1000],
  entriesRange: 100,
  entriesLimit: null,
  savedFilters: [],
};

const defaultState = createSlice({
  name: 'page',
  initialState,
  reducers: {
    setPreferenceId: (state, action: PayloadAction<PageState['preferenceId']>) => {
      state.preferenceId = action.payload;
    },
    setShowLegacy: (state, action: PayloadAction<PageState['showLegacy']>) => {
      state.showLegacy = action.payload;
    },
    setIsFilterVisible: (state, action: PayloadAction<PageState['isFilterVisible']>) => {
      state.isFilterVisible = action.payload;
    },
    setFilterOptions: (
      state,
      action: PayloadAction<{ data: PageState['filterOptions']; storageKey?: WebStorageKeys }>,
    ) => {
      if (action.payload.storageKey) {
        setUserWebStorage(action.payload.storageKey, action.payload.data);
      }
      state.filterOptions = action.payload.data;
    },
    unsetFilterOptions: (state) => {
      [
        WebStorageKeys.IIN_VIEW_FILTER_OPTIONS,
        WebStorageKeys.TRANSACTIONS_VIEW_FILTER_OPTIONS,
        WebStorageKeys.USER_ACCOUNTS_VIEW_FILTER_OPTIONS,
      ].forEach((key) => {
        removeUserWebStorage(key);
      });
      state.filterOptions = [];
    },
    setEntriesRanges: (state, action: PayloadAction<PageState['entriesRanges']>) => {
      state.entriesRanges = action.payload;
    },
    setEntriesRange: (state, action: PayloadAction<PageState['entriesRange']>) => {
      state.entriesRange = action.payload;
    },
    setEntriesLimit: (state, action: PayloadAction<PageState['entriesLimit']>) => {
      state.entriesLimit = action.payload;
    },
    setSavedFilters: (state, action: PayloadAction<PageState['savedFilters']>) => {
      setUserWebStorage(WebStorageKeys.TRANSACTIONS_VIEW_FAVORITE_FILTERS, action.payload);
      state.savedFilters = action.payload;
    },
    unsetSavedFilters: (state) => {
      state.savedFilters = [];
    },
    logout: (state) => {
      // Clear all filter-related storage
      [
        WebStorageKeys.IIN_VIEW_FILTER_OPTIONS,
        WebStorageKeys.TRANSACTIONS_VIEW_FILTER_OPTIONS,
        WebStorageKeys.USER_ACCOUNTS_VIEW_FILTER_OPTIONS,
        WebStorageKeys.TRANSACTIONS_VIEW_FAVORITE_FILTERS,
      ].forEach((key) => {
        removeUserWebStorage(key);
      });
      // Reset state
      state.filterOptions = [];
      state.savedFilters = [];
      state.isFilterVisible = false;
    },
  },
});

export const pageActions = { ...defaultState.actions };

export default defaultState.reducer;
